import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect, useContext } from 'react';

import { Map, List, Iterable } from 'immutable';
import isEmpty from 'lodash/isEmpty';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Linkify from 'components/Generic/Linkify';
import Box from '@mui/material/Box';

import { Display } from 'components';
import { questionTypeSelector } from 'helpers/static';
import CoundownTimer from 'components/Generic/CountdownTimer';
import CandidateApplication from 'records/candidate_application';

import CandidateContext from 'pages/LandingPage/CandidateContext';

const TALKSCORE_MIN_LENGTH = {
  open_ended:        30,
  listen_and_speak:  20,
  listen_and_repeat: 0,
};

const TALKSCORE_MAX_LENGTH = {
  open_ended:        60,
  listen_and_speak:  60,
  listen_and_repeat: 60,
};

const RECORDING_MIN_LENGHT = 0;
const RECORDING_MAX_LENGHT = 120;

function getQuestionTitle(question) {
  switch (question.getIn(['prompt', 'categoryName'])) {
    case 'play_media':
      return t('eos.landing_page.text.play_media.instruction');
    default:
      return question.getIn(['prompt', 'title']);
  }
}

function getInstructions(question) {
  if (isEmpty(question)) return null;

  if (question.getIn(['prompt', 'metadata', 'parameters', 'talk_score'])) {
    const talkScoreType = question.getIn(['prompt', 'metadata', 'parameters', 'talk_score_type']);
    const instructionKey = talkScoreType === 'listen_and_repeat'
      ? 'eos.landing_page.text.talkscore_listen_and_repeat_instructions'
      : 'eos.landing_page.text.talkscore_instructions';

    const translationParams = talkScoreType === 'listen_and_repeat' ? {} : { min: TALKSCORE_MIN_LENGTH[talkScoreType] };

    return (
      <Typography data-testid="answers-talkscore-instructions">
        {t(instructionKey, translationParams)}
      </Typography>
    );
  }

  if (question.getIn(['prompt', 'documentSampleUrl']) || question.getIn(['prompt', 'documentInstruction'])) {
    return (
      <>
        <Display if={question.getIn(['prompt', 'documentSampleUrl'])}>
          <Link href={question.getIn(['prompt', 'documentSampleUrl'])} underline="none" target="_blank" rel="noreferrer">
            <Typography variant="p" ml={1}>
              {t('eos.landing_page.label.view_sample')}
            </Typography>
          </Link>
        </Display>
        <Display if={question.getIn(['prompt', 'documentInstruction'])}>
          { /* TODO: detect link strings */ }
          <Typography variant="p" display="block" gutterBottom>
            {t('eos.landing_page.text.instructions', { text: question.getIn(['prompt', 'documentInstruction']) })}
          </Typography>
        </Display>
      </>
    );
  }

  return null;
}

function getAnswerComponentProps(question, company) {
  switch (question.getIn(['prompt', 'categoryName'])) {
    case 'number':
      return {
        minimum: question.getIn(['prompt', 'metadata', 'parameters', 'minimum']),
        maximum: question.getIn(['prompt', 'metadata', 'parameters', 'maximum']),
      };
    case 'dropdown':
      return {
        dropdownOptions: new List(question.getIn(['prompt', 'metadata', 'parameters', 'dropdown_params']).split(',')),
      };
    case 'multiple choice':
      return {
        options: new List([...question.getIn(['prompt', 'metadata', 'multiple_options'])]),
      };
    case 'audio':
    case 'video':
      const talkscoreEnabled = question.getIn(['prompt', 'metadata', 'parameters', 'talk_score']);
      const talkScoreType = question.getIn(['prompt', 'metadata', 'parameters', 'talk_score_type']);

      const companyTalkscoreMinLength = company.getIn(['parameters', 'talkscoreMinLenght']);

      let min = talkscoreEnabled && TALKSCORE_MIN_LENGTH[talkScoreType] || RECORDING_MIN_LENGHT;
      if (talkscoreEnabled && companyTalkscoreMinLength && talkScoreType !== 'listen_and_repeat') {
        min = companyTalkscoreMinLength;
      }

      const max = (talkscoreEnabled && TALKSCORE_MAX_LENGTH[talkScoreType]) || RECORDING_MAX_LENGHT;

      return { min, max, rerecordable: !talkscoreEnabled };
    case 'play_media':
      return {
        mediaUrl:    question.getIn(['prompt', 'metadata', 'media', 'url']),
        contentType: question.getIn(['prompt', 'metadata', 'media', 'content_type']),
        replayable:  question.getIn(['prompt', 'metadata', 'parameters', 'replayable']),
      };
    case 'file_upload':
      const params = {
        documentName: question.getIn(['prompt', 'documentTag', 'name']),
        recognize:    question.getIn(['prompt', 'documentTag', 'parameters', 'landingPageProcessable']),
      };

      if (question.getIn(['prompt', 'documentTag', 'country'])) params.country = question.getIn(['prompt', 'documentTag', 'country']);

      return params;
    default:
      return {};
  }
}

function getValueFromResponse(response) {
  if (isEmpty(response)) return null;

  return new Map({
    value:       response.get('value'),
    blob:        response.get('blob'),
    blobs:       response.get('blobs'),
    geolocation: response.get('geolocation'),
    duration:    response.get('duration'),
  }); // For inputs, the signedBlobId is not useful
}

function PromptResponse({
  application,
  question = null,
  response = null,
  onChange = null,
  index = null,
  disabled = null,
  country = null,
}) {
  const { company } = useContext(CandidateContext);
  const [state, setState] = useState(new Map());
  const forced = state.get('forced', false);

  const questionType = useMemo(() => question.getIn(['prompt', 'categoryName']), [question]);
  const title = useMemo(() => getQuestionTitle(question), [question]);
  const value = useMemo(() => getValueFromResponse(response), [question, response]);
  const instructions = useMemo(() => getInstructions(question), [question]);
  const identifier = useMemo(() => `${application.id}-${question.getIn(['prompt', 'id'])}`, [question, application]);
  const timerEnabled = useMemo(() => question.getIn(['prompt', 'metadata', 'parameters', 'timer_enabled']), [question]);
  const timer = useMemo(() => question.getIn(['prompt', 'metadata', 'parameters', 'timer']), [question]);

  const AnswerComponent = useMemo(() => questionTypeSelector(questionType), [questionType]);
  const answerComponentProps = useMemo(() => getAnswerComponentProps(question, company), [question, company]);

  function onTimerFinish() {
    setState(state.set('forced', true));
  }

  /**
   * We are going to run this only at initialization (name should not change).
   * If the candidate restores the value to the suggestion, it will not trigger this special case.
   *
   * The timeout is in place to wait for the components to stabilize
   * before submitting the value change.
   */
  useEffect(() => {
    let timeoutId;

    const suggestedAnswer = application.getPromptSuggestedAnswer(question.getIn(['prompt', 'id']));

    if (isEmpty(value) && !isEmpty(suggestedAnswer)) {
      timeoutId = setTimeout(
        () => { onChange(question.getIn(['prompt', 'id']), new Map({ text: suggestedAnswer, suggested: true })); },
        100,
      );
    }

    return () => clearTimeout(timeoutId);
  }, [identifier]);

  return (
    <Grid container item key={identifier} sx={{ mb: 5 }}>
      <Grid item xs={12} mb={2}>
        <Typography variant="body" sx={{ fontWeight: 'medium' }}>
          <label data-testid="answers-question-title" style={{ whiteSpace: 'pre-wrap' }}>
            <Linkify>
              {`${index + 1}. ${title}`}
            </Linkify>
          </label>
        </Typography>

        { instructions }
      </Grid>

      <Grid item xs={12}>
        <AnswerComponent
          onChange={onChange}
          name={question.getIn(['prompt', 'id'])}
          id={identifier}
          value={value}
          disabled={disabled}
          country={country}
          forced={forced}
          {...answerComponentProps}
        />

        <Display if={timerEnabled && timer}>
          <Box sx={{ pt: 2 }}>
            <CoundownTimer id={identifier} time={timer} onFinish={onTimerFinish} paused={disabled} />
          </Box>
        </Display>
      </Grid>
    </Grid>
  );
}

PromptResponse.propTypes = {
  application: PropTypes.instanceOf(CandidateApplication).isRequired,
  question:    PropTypes.objectOf(Iterable),
  response:    PropTypes.objectOf(Iterable),
  onChange:    PropTypes.func,
  index:       PropTypes.number,
  disabled:    PropTypes.bool,
  country:     PropTypes.string,
};

export default PromptResponse;
